import { useState, useEffect, useRef } from 'react'
import { ChevronDown, Printer, Map, RefreshCw } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { downloadPdf } from '../../utils/pdfUtils'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')

const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtDate = s => { if (!s) return ''; try { return new Date(s).toLocaleDateString('pt-BR') } catch { return s } }

function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

const S = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13, color: '#000', background: '#fff', padding: '20px 28px 24px' },
  hdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #1f2937', paddingBottom: 14, marginBottom: 14 },
  hdrLogo: { maxHeight: 80, maxWidth: 180, objectFit: 'contain' },
  hdrCenter: { flex: 1, textAlign: 'center', padding: '0 16px' },
  hdrT1: { fontSize: 20, fontWeight: 700, color: '#1a3c6b' },
  hdrT2: { fontSize: 12, color: '#374151', marginTop: 3 },
  hdrT3: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  clienteBar: { background: '#f3f4f6', border: '1px solid #d1d5db', padding: '10px 16px', fontSize: 13, marginBottom: 16 },
  secTitle: { background: '#f5f5f5', border: '1px solid #000', textAlign: 'center', fontWeight: 700, fontSize: 13, padding: '6px 0', marginTop: 16, marginBottom: 0 },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 4 },
  th: { background: '#e8e8e8', border: '1px solid #ccc', padding: '6px 8px', textAlign: 'center', fontWeight: 700, fontSize: 11 },
  thL: { background: '#e8e8e8', border: '1px solid #ccc', padding: '6px 8px', textAlign: 'left', fontWeight: 700, fontSize: 11 },
  thR: { background: '#e8e8e8', border: '1px solid #ccc', padding: '6px 8px', textAlign: 'right', fontWeight: 700, fontSize: 11 },
  td: { border: '1px solid #e5e7eb', padding: '6px 8px', verticalAlign: 'middle' },
  tdC: { border: '1px solid #e5e7eb', padding: '6px 8px', textAlign: 'center', verticalAlign: 'middle' },
  tdR: { border: '1px solid #e5e7eb', padding: '6px 8px', textAlign: 'right', verticalAlign: 'middle' },
  subtotalRow: { background: '#f5f5f5', fontWeight: 700 },
  resumoRow: { display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderBottom: '1px solid #e5e7eb', fontSize: 13 },
  footer: { textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 20, borderTop: '1px solid #e5e7eb', paddingTop: 10 },
}

function ResumoLinha({ label, valor, sinal }) {
  const cor = sinal === '+' ? '#1e7e34' : '#b02a2a'
  return (
    <div style={S.resumoRow}>
      <span style={{ color: cor, fontWeight: 600 }}>({sinal}) {label}</span>
      <span style={{ color: cor, fontWeight: 600 }}>{brl(valor)}</span>
    </div>
  )
}

export default function FaturaAcerto() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [clientes, setClientes] = useState([])
  const [clienteId, setClienteId] = useState('')
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [loadingC, setLoadingC] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const iframeRef = useRef(null)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingL(false))
  }, [orgId])

  useEffect(() => {
    if (!orgId || !leilaoId) { setClientes([]); return }
    setLoadingC(true)
    setClienteId('')
    setDados(null)
    mapasService.getClientesAcerto(orgId, leilaoId)
      .then(list => { setClientes(list); if (list.length) setClienteId(String(list[0].id)) })
      .catch(() => setClientes([]))
      .finally(() => setLoadingC(false))
  }, [orgId, leilaoId])

  useEffect(() => {
    if (!orgId || !leilaoId || !clienteId) { setDados(null); return }
    setLoading(true)
    mapasService.getFaturaAcertoDados(orgId, leilaoId, clienteId)
      .then(setDados)
      .catch(() => setDados(null))
      .finally(() => setLoading(false))
  }, [orgId, leilaoId, clienteId])

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const logoOrgSrc = organizadorAtivo?.logo_url
    ? (organizadorAtivo.logo_url.startsWith('http') ? organizadorAtivo.logo_url
        : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`)
    : null

  const handleImprimir = async () => {
    if (!orgId || !leilaoId || !clienteId) return
    setLoadingPdf(true)
    try {
      const blob = await mapasService.getFaturaAcertoPdf(orgId, leilaoId, clienteId)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) {
      alert('Erro ao gerar PDF: ' + (err.response?.statusText || err.message))
    } finally { setLoadingPdf(false) }
  }
  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador para visualizar a Fatura Acerto.
    </div>
  )

  const resumo = dados?.resumo
  const aReceber = resumo ? resumo.saldo >= 0 : true
  const clienteNome = clientes.find(c => String(c.id) === clienteId)?.nome

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Map className="w-5 h-5 text-fuchsia-500" />
        <h1 className="text-lg font-semibold text-gray-800">Fatura Acerto</h1>
      </div>

      {/* Seletores */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Leilão" value={leilaoId} onChange={setLeilaoId} disabled={loadingL} className="flex-[2] min-w-[200px]">
            {loadingL ? <option>Carregando...</option>
              : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome} — {fmtDate(l.data_leilao)}</option>)
            }
          </Select>
          <Select label="Cliente" value={clienteId} onChange={setClienteId} disabled={loadingC || !clientes.length} className="flex-[2] min-w-[220px]">
            {loadingC ? <option>Carregando...</option>
              : clientes.length ? clientes.map(c => <option key={c.id} value={String(c.id)}>{c.nome}{c.cpf_cnpj ? ` — ${c.cpf_cnpj}` : ''}</option>)
              : <option>Nenhum cliente com movimento</option>
            }
          </Select>
          <div className="flex items-end gap-2 ml-auto self-end">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleImprimir} disabled={!dados || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} />
              {loadingPdf ? 'Gerando PDF...' : 'Imprimir'}
            </button>
          </div>
        </div>
      </div>

      {/* Preview */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : !dados ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Selecione um leilão e um cliente para visualizar a fatura de acerto
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div style={S.page}>
            {/* 1. Header */}
            <div style={S.hdr}>
              <div style={{ minWidth: 140 }}>
                {logoOrgSrc ? <img src={logoOrgSrc} alt="logo" style={S.hdrLogo} />
                  : <span style={{ fontWeight: 700, fontSize: 14 }}>{dados.organizador?.nome}</span>}
              </div>
              <div style={S.hdrCenter}>
                <div style={S.hdrT1}>FATURA DE ACERTO</div>
                <div style={S.hdrT2}>{dados.organizador?.nome}</div>
                <div style={S.hdrT3}>{dados.leilao?.nome} — {fmtDate(dados.leilao?.data_leilao)}</div>
              </div>
              <div style={{ minWidth: 140 }} />
            </div>

            <div style={S.clienteBar}>
              <strong>Cliente:</strong> {dados.cliente?.nome} &nbsp;&nbsp; <strong>CPF/CNPJ:</strong> {dados.cliente?.cpf_cnpj || '-'}
            </div>

            {/* 2. COMO VENDEDOR */}
            {dados.lotes_venda?.length > 0 && (
              <>
                <div style={S.secTitle}>COMO VENDEDOR</div>
                <table style={S.tbl}>
                  <thead>
                    <tr>
                      <th style={S.th}>Lote</th>
                      <th style={S.thL}>Comprador</th>
                      <th style={S.thR}>Valor Venda</th>
                      <th style={S.thR}>Sinal Recebido</th>
                      <th style={S.thR}>Comissão de Venda</th>
                      <th style={S.thR}>Despesas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.lotes_venda.map(l => (
                      <tr key={l.id}>
                        <td style={S.tdC}>{l.numero_lote}{l.percentual_participacao != null ? ` (${l.percentual_participacao}%)` : ''}</td>
                        <td style={S.td}>{l.comprador_nome}</td>
                        <td style={S.tdR}>{brl(l.vv)}</td>
                        <td style={S.tdR}>{brl(l.sinal)}{l.eh_avista ? ' (À vista)' : ''}</td>
                        <td style={S.tdR}>{brl(l.comissao_val)}</td>
                        <td style={S.tdR}>{brl(l.desp)}</td>
                      </tr>
                    ))}
                    <tr style={S.subtotalRow}>
                      <td style={S.td} colSpan={5}>Subtotal recebido (sinal das vendas)</td>
                      <td style={S.tdR}>{brl(resumo.sinal_vendas)}</td>
                    </tr>
                    <tr style={S.subtotalRow}>
                      <td style={S.td} colSpan={5}>Subtotal despesas</td>
                      <td style={S.tdR}>{brl(dados.lotes_venda.reduce((s, l) => s + (l.desp || 0), 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            {/* 3. COMO COMPRADOR */}
            {dados.lotes_compra?.length > 0 && (
              <>
                <div style={S.secTitle}>COMO COMPRADOR</div>
                <table style={S.tbl}>
                  <thead>
                    <tr>
                      <th style={S.th}>Lote</th>
                      <th style={S.thL}>Vendedor</th>
                      <th style={S.thR}>Valor Compra</th>
                      <th style={S.thR}>Sinal a Pagar</th>
                      <th style={S.thR}>Comissão de Compra</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.lotes_compra.map(l => (
                      <tr key={l.id}>
                        <td style={S.tdC}>{l.numero_lote}{l.percentual_participacao != null ? ` (${l.percentual_participacao}%)` : ''}</td>
                        <td style={S.td}>{l.vendedor_nome}</td>
                        <td style={S.tdR}>{brl(l.vv)}</td>
                        <td style={S.tdR}>{brl(l.sinal)}{l.eh_avista ? ' (À vista)' : ''}</td>
                        <td style={S.tdR}>{brl(l.comissao_val)}</td>
                      </tr>
                    ))}
                    <tr style={S.subtotalRow}>
                      <td style={S.td} colSpan={4}>Subtotal a pagar (sinal das compras)</td>
                      <td style={S.tdR}>{brl(resumo.sinal_compras)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* Parcelamento OPCIONAL da comissão de compra (fluxo cavalo) */}
                {dados.lotes_compra.some(l => l.comissao_parcelamento) && (
                  <div style={{ marginTop: 8, marginBottom: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4 }}>Parcelamento da comissão de compra</div>
                    {dados.lotes_compra.filter(l => l.comissao_parcelamento).map(l => {
                      const sch = l.comissao_parcelamento
                      return (
                        <div key={`parc-${l.id}`} style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 2 }}>Lote {l.numero_lote}</div>
                          <table style={S.tbl}>
                            <thead>
                              <tr>
                                <th style={S.thL}>Descrição</th>
                                <th style={S.th}>Vencimento</th>
                                <th style={S.thR}>Valor</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sch.linhas.map((ln, i) => (
                                <tr key={i}>
                                  <td style={S.td}>{ln.rotulo}</td>
                                  <td style={S.tdC}>{ln.data ? fmtDate(ln.data) : '—'}</td>
                                  <td style={S.tdR}>{brl(ln.valor)}</td>
                                </tr>
                              ))}
                              <tr style={S.subtotalRow}>
                                <td style={S.td} colSpan={2}>Total</td>
                                <td style={S.tdR}>{brl(sch.total)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      )
                    })}
                  </div>
                )}
              </>
            )}

            {/* 4. LOTES EM DEFESA */}
            {dados.lotes_defesa?.length > 0 && (
              <>
                <div style={S.secTitle}>LOTES EM DEFESA</div>
                <table style={S.tbl}>
                  <thead>
                    <tr>
                      <th style={S.th}>Lote</th>
                      <th style={S.thL}>Comprador</th>
                      <th style={S.thR}>Valor Venda</th>
                      <th style={S.thR}>Comissão de Defesa</th>
                      <th style={S.thR}>Despesas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.lotes_defesa.map(l => (
                      <tr key={l.id}>
                        <td style={S.tdC}>{l.numero_lote}</td>
                        <td style={S.td}>{l.comprador_nome}</td>
                        <td style={S.tdR}>{brl(l.vv)}</td>
                        <td style={S.tdR}>{brl(l.defesa_val)}</td>
                        <td style={S.tdR}>{brl(l.desp)}</td>
                      </tr>
                    ))}
                    <tr style={S.subtotalRow}>
                      <td style={S.td} colSpan={4}>Subtotal comissão de defesa</td>
                      <td style={S.tdR}>{brl(resumo.comissao_defesa)}</td>
                    </tr>
                    <tr style={S.subtotalRow}>
                      <td style={S.td} colSpan={4}>Subtotal despesas</td>
                      <td style={S.tdR}>{brl(dados.lotes_defesa.reduce((s, l) => s + (l.desp || 0), 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            {/* 5. LOTES NÃO VENDIDOS — grid compacto (4 pares Lote/Despesas
                por linha) em vez de uma linha por lote, pra não ocupar uma
                altura de página inteira em leilões com muitos lotes não
                vendidos. */}
            {dados.lotes_nao_vendidos?.length > 0 && (
              <>
                <div style={S.secTitle}>LOTES NÃO VENDIDOS</div>
                <table style={S.tbl}>
                  <thead>
                    <tr>
                      <th style={S.th}>Lote</th><th style={S.thR}>Despesas</th>
                      <th style={S.th}>Lote</th><th style={S.thR}>Despesas</th>
                      <th style={S.th}>Lote</th><th style={S.thR}>Despesas</th>
                      <th style={S.th}>Lote</th><th style={S.thR}>Despesas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: Math.ceil(dados.lotes_nao_vendidos.length / 4) }).map((_, rowIdx) => {
                      const chunk = dados.lotes_nao_vendidos.slice(rowIdx * 4, rowIdx * 4 + 4)
                      const cells = chunk.flatMap(l => ([
                        <td key={`n-${l.id}`} style={S.tdC}>{l.numero_lote}</td>,
                        <td key={`d-${l.id}`} style={S.tdR}>{brl(l.desp)}</td>,
                      ]))
                      const empties = Array.from({ length: (4 - chunk.length) * 2 }).map((_, i) => (
                        <td key={`empty-${i}`} style={S.td}></td>
                      ))
                      return <tr key={rowIdx}>{cells}{empties}</tr>
                    })}
                  </tbody>
                </table>
                <div style={{ textAlign: 'right', fontWeight: 700, fontSize: 12, marginBottom: 4 }}>
                  Subtotal despesas: {brl(dados.lotes_nao_vendidos.reduce((s, l) => s + (l.desp || 0), 0))}
                </div>
              </>
            )}

            {/* 6. RESUMO DO ACERTO — agrupado em A PAGAR / A RECEBER em vez de
                lista intercalada de (+)/(−), pra facilitar conferir cada
                direção separadamente antes do saldo final. */}
            <div style={S.secTitle}>RESUMO DO ACERTO</div>
            <div style={{ border: '1px solid #000', borderTop: 'none', marginBottom: 12 }}>
              {(dados.lotes_compra?.length > 0 || dados.lotes_venda?.length > 0 || resumo.despesas > 0 || dados.lotes_defesa?.length > 0) && (
                <>
                  <div style={{ background: '#e8e8e8', textAlign: 'center', fontWeight: 700, fontSize: 12, padding: '4px 0' }}>A PAGAR</div>
                  {dados.lotes_compra?.length > 0 && <ResumoLinha label="Sinal das Compras" valor={resumo.sinal_compras} sinal="−" />}
                  {dados.lotes_compra?.length > 0 && <ResumoLinha label="Comissão de Compra" valor={resumo.comissao_compra} sinal="−" />}
                  {dados.lotes_venda?.length > 0 && <ResumoLinha label="Comissão de Venda" valor={resumo.comissao_venda} sinal="−" />}
                  {resumo.despesas > 0 && <ResumoLinha label="Despesas dos Lotes" valor={resumo.despesas} sinal="−" />}
                  {dados.lotes_defesa?.length > 0 && <ResumoLinha label="Comissão de Defesa" valor={resumo.comissao_defesa} sinal="−" />}
                  <div style={{ ...S.resumoRow, ...S.subtotalRow, borderTop: '1px solid #000' }}>
                    <span>Total a Pagar</span>
                    <span>{brl(resumo.total_a_pagar)}</span>
                  </div>
                </>
              )}
              {dados.lotes_venda?.length > 0 && (
                <>
                  <div style={{ background: '#e8e8e8', textAlign: 'center', fontWeight: 700, fontSize: 12, padding: '4px 0' }}>A RECEBER</div>
                  <ResumoLinha label="Sinal das Vendas" valor={resumo.sinal_vendas} sinal="+" />
                  <div style={{ ...S.resumoRow, ...S.subtotalRow, borderTop: '1px solid #000', borderBottom: 'none' }}>
                    <span>Total a Receber</span>
                    <span>{brl(resumo.total_a_receber)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Equação visível ligando os subtotais ao saldo final — evita
                mostrar só o resultado pronto na caixa colorida sem o
                "porquê". Segue a mesma ordem de exibição dos blocos acima
                (A PAGAR primeiro, depois A RECEBER). */}
            <div style={{ textAlign: 'right', fontSize: 13, marginBottom: 8 }}>
              Total a Pagar ({brl(resumo.total_a_pagar)})  −  Total a Receber ({brl(resumo.total_a_receber)})  =  <strong>{brl((resumo.total_a_pagar || 0) - (resumo.total_a_receber || 0))}</strong>
            </div>

            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 18px', borderRadius: 8, marginBottom: 20,
              background: aReceber ? '#e6f4ea' : '#fdecea',
              border: `2px solid ${aReceber ? '#1e7e34' : '#b02a2a'}`,
            }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: aReceber ? '#1e7e34' : '#b02a2a' }}>SALDO DO ACERTO</span>
              <span style={{ fontWeight: 700, fontSize: 16, color: aReceber ? '#1e7e34' : '#b02a2a' }}>
                {brl(Math.abs(resumo.saldo))} — {aReceber ? 'A RECEBER pelo cliente' : 'A PAGAR pelo cliente'}
              </span>
            </div>

            {/* 7. Recibo */}
            <div style={{ borderTop: '1px solid #1a3c6b', paddingTop: 14 }}>
              <div style={{ textAlign: 'center', fontWeight: 700, color: '#1a3c6b', marginBottom: 10 }}>RECIBO</div>
              <p style={{ fontSize: 13, lineHeight: 1.6 }}>
                {aReceber ? 'Pagamos' : 'Recebemos'} do(a) Sr(a). <strong>{dados.cliente?.nome}</strong> a importância de{' '}
                <strong>{brl(Math.abs(resumo.saldo))}</strong> referente ao acerto do leilão <strong>{dados.leilao?.nome}</strong>{' '}
                conforme demonstrativo acima.
              </p>
              <div style={{ marginTop: 40, textAlign: 'center' }}>
                <div style={{ borderTop: '1px solid #000', width: 280, margin: '0 auto' }} />
                <div style={{ fontSize: 12, fontWeight: 700, marginTop: 4 }}>{dados.organizador?.nome}</div>
              </div>
            </div>

            <div style={S.footer}>www.rematei.com.br</div>
          </div>
        </div>
      )}

      {/* Modal PDF */}
      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 sm:p-4">
          <div className="bg-white w-full max-w-5xl flex flex-col rounded-t-2xl sm:rounded-xl shadow-2xl" style={{ height: '92dvh' }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl sm:rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800 text-sm truncate flex-1 min-w-0">Fatura Acerto — {clienteNome}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => downloadPdf(pdfUrl, `Fatura Acerto - ${clienteNome || ''}.pdf`)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
                  Salvar PDF
                </button>
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer size={15} /> Imprimir
                </button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Fatura Acerto" style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
